import { Stack, Title, Paper, Text, Table, Select, LoadingOverlay, useMantineTheme } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState, useEffect, Fragment } from 'react';
import { getUnitPriceList, UnitPriceListResponse } from '../../../services/objectmodelservice';
import useTransientStore from '../../../data/transcientStore';
import { useTranslation } from "../../../hooks/useTranslation";

interface UnitPricesTabProps {
    form: any;
    canModify?: boolean;
}

export default function UnitPricesTab({ form, canModify = true }: UnitPricesTabProps) {
    const theme = useMantineTheme();
    const headerColor = theme.colors.gray[2];
    const headerText = theme.colors.dark[7];
    
    const [priceListData, setPriceListData] = useState<UnitPriceListResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    const currentUserId = useTransientStore((state) => state.currentUserId);
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const addrIdFromStore = selectedAddressSystem?.id || 0;
    const { getTranslation } = useTranslation({ pageId: 349, addressSystemId: addrIdFromStore || 0, autoLoad: true });
    const objectId = form.values.id;
    const selectedContractId = form.values.job ? parseInt(form.values.job) : null;

    useEffect(() => {
        const fetchPriceList = async () => {
            if (!objectId || objectId <= 0 || !currentUserId || !selectedAddressSystem?.id) return;

            setIsLoading(true);
            setError(null);
            
            try {
                const data = await getUnitPriceList(objectId, currentUserId, selectedAddressSystem.id, selectedContractId ?? undefined);
                setPriceListData(data);
            } catch (err) {
                console.error('Failed to fetch unit price list:', err);
                setError(
                    getTranslation("MESSAGE_UNIT_PRICES_LOAD_FAILED") ||
                    'Failed to load unit prices'
                );
                notifications.show({
                    title: getTranslation("TITLE_ERROR") || 'Error',
                    message:
                        getTranslation("MESSAGE_UNIT_PRICES_LOAD_FAILED") ||
                        'Failed to load unit prices',
                    color: 'red',
                    autoClose: 5000,
                    position: 'top-center',
                    classNames: {
                        root: 'notification-error',
                    },
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchPriceList();
    }, [objectId, currentUserId, selectedAddressSystem?.id, selectedContractId]);

    const handleContractChange = (value: string | null) => {
        form.setFieldValue('job', value || '');
    };

    const formatPrice = (price: number, unit: string): string => {
        return `${price.toFixed(2)} €/${unit}`;
    };

    const getItemDisplayNumber = (itemNumber: number, itemSubnumber: number, itemCode: string | null): string => {
        if (priceListData?.useWorkItemCode && itemCode) {
            return itemCode;
        }
        return `${itemNumber}.${itemSubnumber}`;
    };

    return (
        <>
            <Stack gap="lg" style={{ position: 'relative' }}>
                <LoadingOverlay 
                    visible={isLoading} 
                    zIndex={1000}
                    overlayProps={{ blur: 2 }}
                />
                <Title order={4} c="blue">
                    {getTranslation("TITLE_UNIT_PRICES") || "Unit Prices"}
                    {/* {priceListData?.objectName && ` - ${priceListData.objectName}`} */}
                </Title>

                {error && (
                    <Text c="red" size="sm">{error}</Text>
                )}

                    {/* Contract Selection - only show if multiple contracts */}
                    {priceListData && priceListData.availableContracts.length > 1 && (
                        <Paper p="md" withBorder>
                            <Select
                                label={getTranslation("LABEL_SELECT_CONTRACT") || "Select Contract"}
                                placeholder={getTranslation("PLACEHOLDER_CHOOSE_A_CONTRACT") || "Choose a contract"}
                                data={priceListData.availableContracts.map(c => ({
                                    value: c.id.toString(),
                                    label: c.name
                                }))}
                                value={selectedContractId?.toString() ?? null}
                                onChange={handleContractChange}
                                clearable={false}
                            />
                        </Paper>
                    )}

                    {/* Price Groups and Items Table */}
                    <Paper withBorder>
                        <Table striped highlightOnHover>
                            <Table.Thead>
                                <Table.Tr style={{ backgroundColor: headerColor, color: headerText }}>
                                    <Table.Th style={{ color: headerText }}>
                                        {getTranslation("LABEL_WORK_PHASE") || "Work Phase"}
                                    </Table.Th>
                                    <Table.Th style={{ textAlign: 'center', color: headerText, borderLeft: '1px solid #ddd' }}>
                                        {getTranslation("LABEL_UNIT") || "Unit"}
                                    </Table.Th>
                                    <Table.Th style={{ textAlign: 'center', color: headerText, borderLeft: '1px solid #ddd' }}>
                                        {getTranslation("LABEL_PRICE") || "Price"}
                                    </Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {!priceListData || priceListData.availableContracts.length === 0 ? (
                                    <Table.Tr>
                                        <Table.Td colSpan={3}>
                                            <Text c="blue" ta="center" py="xl" size="sm" fw={500}>
                                                {getTranslation("LABEL_NO_UNIT_PRICES_AVAILABLE") ||
                                                    "No unit prices available"}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ) : !selectedContractId && priceListData.availableContracts.length > 1 ? (
                                    <Table.Tr>
                                        <Table.Td colSpan={3}>
                                            <Text c="blue" ta="center" py="xl" size="sm" fw={500}>
                                                {getTranslation("LABEL_NO_UNIT_PRICES_AVAILABLE") ||
                                                    "No unit prices available"}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ) : !selectedContractId && priceListData.availableContracts.length === 1 ? (
                                    <Table.Tr>
                                        <Table.Td colSpan={3}>
                                            <Text c="blue" ta="center" py="xl" size="sm" fw={500}>
                                                {getTranslation("LABEL_NO_UNIT_PRICES_AVAILABLE") ||
                                                    "No unit prices available"}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ) : priceListData && selectedContractId && priceListData.priceGroups.length === 0 ? (
                                    <Table.Tr>
                                        <Table.Td colSpan={3}>
                                            <Text c="blue" ta="center" py="xl" size="sm" fw={500}>
                                                {getTranslation("LABEL_NO_UNIT_PRICES_AVAILABLE") ||
                                                    "No unit prices available"}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ) : (
                                    priceListData && selectedContractId && priceListData.priceGroups.map((group) => (
                                        <Fragment key={`group-${group.id}`}>
                                            {/* Price Group Header */}
                                            <Table.Tr style={{ backgroundColor: headerColor }}>
                                                <Table.Td colSpan={3}>
                                                    <Text fw={700}>{group.name}</Text>
                                                </Table.Td>
                                            </Table.Tr>
                                            {/* Price Items */}
                                            {group.items.map((item) => (
                                                <Table.Tr key={`item-${item.id}`}>
                                                    <Table.Td>
                                                        <Text size="sm">{item.name}</Text>
                                                    </Table.Td>
                                                    <Table.Td style={{ borderLeft: '1px solid #ddd', textAlign: 'center' }}>
                                                        <Text size="sm">{item.unitName}</Text>
                                                    </Table.Td>
                                                    <Table.Td style={{ borderLeft: '1px solid #ddd', textAlign: 'center' }}>
                                                        <Text size="sm" fw={500}>
                                                            {formatPrice(item.unitPrice, item.unitName)}
                                                        </Text>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Fragment>
                                    ))
                                )}
                            </Table.Tbody>
                        </Table>
                    </Paper>
                </Stack>
        </>
    );
}